/**
 * Application-wide configuration constants.
 *
 * Zero-hardcoding rule: every literal that identifies app behaviour, storage
 * keys, locale, theme, pagination, SEO copy or formatting lives here — never
 * inline in a component or a handler.
 */

export const APP_NAME = "bizy" as const;
export const APP_TAGLINE = "Make Business Eazy." as const;
export const APP_DESCRIPTION = "Point of sales, orders and inventory for multi-tenant businesses." as const;
export const APP_VERSION = "0.1.0" as const;

/* ------------------------------------------------------------------ locales */

export const LOCALES = {
  en: "en",
  id: "id",
} as const;

export type Locale = (typeof LOCALES)[keyof typeof LOCALES];

export const SUPPORTED_LOCALES: readonly Locale[] = [LOCALES.en, LOCALES.id];

export const DEFAULT_LOCALE: Locale = LOCALES.id;
export const FALLBACK_LOCALE: Locale = LOCALES.en;

export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  id: "Bahasa Indonesia",
};

export const LOCALE_HTML_LANG: Record<Locale, string> = {
  en: "en",
  id: "id",
};

export const LOCALE_OG_LOCALE: Record<Locale, string> = {
  en: "en_US",
  id: "id_ID",
};

/* ------------------------------------------------------------------- themes */

export const THEME_KEYS = {
  light: "light",
  dark: "dark",
  system: "system",
} as const;

export type ThemeKey = (typeof THEME_KEYS)[keyof typeof THEME_KEYS];

export const DEFAULT_THEME: ThemeKey = THEME_KEYS.system;

export const THEME_COLOR_LIGHT = "#ffffff" as const;
export const THEME_COLOR_DARK = "#0b0f0f" as const;

/* -------------------------------------------------- browser persistence keys */

export const STORAGE_KEYS = {
  theme: "bizy.theme",
  locale: "bizy.locale",
  sidebarCollapsed: "bizy.sidebar-collapsed",
  timezone: "bizy.timezone",
  lastWorkspace: "bizy.last-workspace",
  pendingInvite: "bizy.pending-invite",
  invoicePageSize: "bizy.invoice-page-size",
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

/* --------------------------------------------------------- regional defaults */

export const DEFAULT_TIMEZONE_FALLBACK = "Asia/Jakarta" as const;

export const DEFAULT_CURRENCY = "IDR" as const;

export const CURRENCY_DISPLAY: Record<string, { locale: string; fractionDigits: number }> = {
  IDR: { locale: "id-ID", fractionDigits: 0 },
  USD: { locale: "en-US", fractionDigits: 2 },
};

export const DEFAULT_CURRENCY_DISPLAY = { locale: "en-US", fractionDigits: 2 } as const;

export const DATE_FORMATS = {
  date: "dd MMM yyyy",
  dateTime: "dd MMM yyyy HH:mm",
  dateTimeWithSeconds: "dd MMM yyyy HH:mm:ss",
  time: "HH:mm",
  isoDate: "yyyy-MM-dd",
  monthKey: "yyyy-MM",
} as const;

/* ----------------------------------------------------------------- pagination */

export const DEFAULT_PAGE_SIZE = 20 as const;
export const PAGE_SIZE_OPTIONS: readonly number[] = [10, 20, 50, 100];
export const MAX_PAGE_SIZE = 100 as const;

/* ------------------------------------------------------------- query timings */

export const SEARCH_DEBOUNCE_MS = 300 as const;
export const API_TIMEOUT_MS = 20_000 as const;
export const QUERY_STALE_TIME_MS = 30_000 as const;
export const QUERY_GC_TIME_MS = 300_000 as const;
export const TOAST_DURATION_MS = 4_000 as const;

export const QUERY_KEYS = {
  me: "me",
  workspaces: "workspaces",
  workspace: "workspace",
  members: "members",
  products: "products",
  variants: "variants",
  orders: "orders",
  order: "order",
  invoices: "invoices",
  invoice: "invoice",
  logs: "logs",
  analytics: "analytics",
  superboard: "superboard",
} as const;

/* ---------------------------------------------------------------- validation */

export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const SLUG_MIN_LENGTH = 3 as const;
export const SLUG_MAX_LENGTH = 48 as const;
export const NAME_MIN_LENGTH = 2 as const;
export const NAME_MAX_LENGTH = 80 as const;
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export const PHONE_PATTERN = /^[+()\-\s0-9]{6,20}$/;
export const SKU_PATTERN = /^[A-Za-z0-9._-]{1,32}$/;

/* ----------------------------------------------------------------------- SEO */

export const SEO_DEFAULTS = {
  titleTemplate: `%s · ${APP_NAME}`,
  ogType: "website",
  twitterCard: "summary_large_image",
  ogImagePath: "/og/bizy.svg",
  twitterHandle: "@bizy",
  robots: "index, follow",
  publicPathPrefix: "/",
} as const;

export const SEO_COPY: Record<Locale, { title: string; description: string; keywords: string }> = {
  en: {
    title: `${APP_NAME} — Point of Sales & Order Management`,
    description:
      "bizy is a multi-tenant point of sales, order and inventory workspace for rental, catering and retail businesses. Run checkout, track down payments and share invoices in seconds.",
    keywords: "point of sales, POS, order management, inventory, invoice, multi-tenant, rental",
  },
  id: {
    title: `${APP_NAME} — Kasir & Manajemen Pesanan`,
    description:
      "bizy adalah aplikasi kasir, pesanan, dan inventaris multi-tenant untuk bisnis rental, katering, dan retail. Proses checkout, kelola DP, dan bagikan invoice dalam hitungan detik.",
    keywords: "aplikasi kasir, POS, manajemen pesanan, inventaris, invoice, multi-tenant, rental",
  },
};

/* ------------------------------------------------------------------- auth */

export const FIREBASE = {
  googleProviderId: "google.com",
  /** Asymmetric JWKS for Firebase ID tokens; verified by the Worker. */
  jwksUrl: "https://www.googleapis.com/service_accounts/v1/jwk/securetoken@system.gserviceaccount.com",
  issuer: (projectId: string) => `https://securetoken.google.com/${projectId}`,
  requiredRoleClaim: "authenticated",
  requiredCustomClaimKey: "role",
  popupRedirectFallback: true,
} as const;

export const AUTH_DEFAULTS = {
  /** Refresh the ID token this many seconds before Supabase sees it expire. */
  tokenRefreshSkewSeconds: 60,
  /** Where the login page returns to after OAuth. */
  postLoginRoute: "/dashboard",
} as const;

/* ------------------------------------------------------------------- branding */

export const BRAND = {
  name: APP_NAME,
  mark: "b",
  primaryHue: "emerald",
  supportHue: "teal",
  accentHue: "cyan",
} as const;

export const EXTERNAL_LINKS = {
  docs: "https://kokonutui.com/docs",
  supabase: "https://supabase.com",
  cloudflare: "https://developers.cloudflare.com/pages/",
} as const;
